import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import translations, { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './translations';

const STORAGE_KEY = 'algorcode_language';
const IP_LOOKUP_URL = 'https://ipapi.co/json/';

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key) => key,
  resolved: false
});

const interpolate = (value, params = {}) =>
  Object.keys(params).reduce(
    (acc, paramKey) => acc.replace(new RegExp(`{${paramKey}}`, 'g'), params[paramKey]),
    value
  );

const getNestedValue = (lang, key) => {
  const segments = key.split('.');
  return segments.reduce((acc, segment) => (acc && acc[segment] !== undefined ? acc[segment] : undefined), translations[lang]);
};

const translate = (language, key, params) => {
  const fallback = getNestedValue(DEFAULT_LANGUAGE, key);
  const localized = getNestedValue(language, key);
  const value = localized !== undefined ? localized : fallback !== undefined ? fallback : key;

  if (typeof value === 'string') {
    return interpolate(value, params);
  }

  return value;
};

const mapLanguageCode = (code = '') => {
  const normalized = code.toLowerCase();
  if (normalized.startsWith('pt')) return 'pt';
  if (normalized.startsWith('en')) return 'en';
  return null;
};

const mapCountryToLanguage = (countryCode = '') => {
  const normalized = countryCode.toLowerCase();
  const portugueseCountries = ['br', 'pt', 'ao', 'mz', 'st', 'gw', 'cv', 'tl'];
  if (portugueseCountries.includes(normalized)) return 'pt';
  return 'en';
};

const detectLanguage = async () => {
  try {
    const response = await fetch(IP_LOOKUP_URL, { method: 'GET' });
    if (response.ok) {
      const data = await response.json();
      if (data.country_code) {
        const mapped = mapCountryToLanguage(data.country_code);
        if (mapped) return mapped;
      }
      if (data.languages) {
        const firstLang = String(data.languages).split(',')[0];
        const mapped = mapLanguageCode(firstLang);
        if (mapped) return mapped;
      }
    }
  } catch (error) {
    // Silent fallback to browser language or default
  }

  const browserLang = navigator.language || navigator.userLanguage || '';
  return mapLanguageCode(browserLang) || DEFAULT_LANGUAGE;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LANGUAGES.includes(saved)) {
      setLanguage(saved);
      setResolved(true);
      return;
    }

    let cancelled = false;
    const resolveLanguage = async () => {
      const detected = await detectLanguage();
      if (cancelled) return;
      setLanguage(detected);
      setResolved(true);
      localStorage.setItem(STORAGE_KEY, detected);
    };

    resolveLanguage();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', language);
    }
  }, [language]);

  const setAndPersist = (newLanguage) => {
    if (!SUPPORTED_LANGUAGES.includes(newLanguage)) {
      return;
    }
    setLanguage(newLanguage);
    localStorage.setItem(STORAGE_KEY, newLanguage);
  };

  const contextValue = useMemo(
    () => ({
      language,
      setLanguage: setAndPersist,
      t: (key, params) => translate(language, key, params),
      resolved,
      translations: translations[language]
    }),
    [language, resolved]
  );

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
};

export const useTranslation = () => useContext(LanguageContext);
